import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { useWindowSize } from '@/shared/hooks/useWindowSize';
import goldIcon from '@/assets/header/gold.webp';
import energyIcon from '@/assets/header/energy.webp';
import giftIcon from '@/assets/ranking/gift.webp';

// Types for leaderboard rewards
export interface LeaderboardRewardData {
  rank: number;
  period: 'weekly' | 'monthly';
  leaderboardType: 'pve' | 'pvp';
  gold: number;
  energyBoost: number;
  durationDays: number;
}

interface LeaderboardRewardModalProps {
  isOpen: boolean;
  reward: LeaderboardRewardData | null;
  onClose: () => void;
}

// Get rank tier info based on rank
const getRankTier = (rank: number) => {
  if (rank === 1) {
    return {
      title: 'Champion',
      emoji: '👑',
      color: '#FFD700',
      bgGradient:
        'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      glowColor: 'rgba(255, 215, 0, 0.6)',
      borderColor: '#FFE066',
    };
  }
  if (rank >= 2 && rank <= 3) {
    return {
      title: 'Elite',
      emoji: '⚔️',
      color: '#C0C0C0',
      bgGradient:
        'linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 50%, #A8A8A8 100%)',
      glowColor: 'rgba(192, 192, 192, 0.5)',
      borderColor: '#D4D4D4',
    };
  }
  return {
    title: 'Contender',
    emoji: '🏆',
    color: '#CD7F32',
    bgGradient:
      'linear-gradient(135deg, #CD7F32 0%, #B87333 50%, #8B4513 100%)',
    glowColor: 'rgba(205, 127, 50, 0.5)',
    borderColor: '#DDA15E',
  };
};

// Floating particle for ambient effect
const FloatingParticle = ({ delay, size }: { delay: number; size: number }) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      left: `${Math.random() * 100}%`,
      background: `radial-gradient(circle, rgba(255, 215, 0, 0.6) 0%, transparent 70%)`,
    }}
    initial={{ y: '100%', opacity: 0 }}
    animate={{
      y: '-100%',
      opacity: [0, 0.8, 0],
    }}
    transition={{
      delay,
      duration: 4 + Math.random() * 2,
      repeat: Infinity,
      ease: 'linear',
    }}
  />
);

export const LeaderboardRewardModal = ({
  isOpen,
  reward,
  onClose,
}: LeaderboardRewardModalProps) => {
  const { width, height } = useWindowSize();

  if (!reward) return null;

  const tier = getRankTier(reward.rank);
  const periodLabel = reward.period === 'weekly' ? 'Weekly' : 'Monthly';
  const modeLabel = reward.leaderboardType === 'pve' ? 'PVE' : 'PVP';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with radial glow */}
          <motion.div
            className="fixed inset-0 z-[120]"
            style={{
              background: `radial-gradient(circle at center, ${tier.glowColor} 0%, rgba(0, 0, 0, 0.9) 70%)`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Floating particles layer */}
          <div className="fixed inset-0 z-[121] pointer-events-none overflow-hidden">
            {Array.from({ length: 20 }).map((_, i) => (
              <FloatingParticle
                key={i}
                delay={i * 0.2}
                size={4 + Math.random() * 8}
              />
            ))}
          </div>

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[122] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none px-4">
            <motion.div
              className="relative w-full max-w-[360px] flex flex-col items-center isolate rounded-3xl pointer-events-auto overflow-hidden"
              style={{
                background:
                  'linear-gradient(180deg, rgba(15, 8, 30, 0.98) 0%, rgba(30, 15, 50, 0.95) 100%)',
                border: `3px solid ${tier.borderColor}`,
                boxShadow: `
                  0 0 60px ${tier.glowColor},
                  0 0 120px ${tier.glowColor},
                  inset 0 1px 0 rgba(255, 255, 255, 0.1),
                  0 25px 50px rgba(0, 0, 0, 0.5)
                `,
              }}
              initial={{ opacity: 0, scale: 0.3, y: 100, rotateX: 45 }}
              animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
              exit={{ opacity: 0, scale: 0.5, y: 50 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
              {/* Top glow bar */}
              <motion.div
                className="absolute top-0 left-0 right-0 h-1"
                style={{ background: tier.bgGradient }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              />

              {/* Content */}
              <div className="relative flex flex-col items-center gap-4 w-full p-6 pt-8 z-10">
                {/* Gift Icon with glow */}
                <motion.div
                  className="relative"
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    delay: 0.2,
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                  }}
                >
                  <div
                    className="absolute inset-0 blur-xl"
                    style={{
                      background: tier.glowColor,
                      transform: 'scale(1.5)',
                    }}
                  />
                  <img
                    src={giftIcon}
                    alt="Reward"
                    className="relative w-20 h-20"
                  />
                </motion.div>

                {/* Period & Mode Badge */}
                <motion.div
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      background:
                        reward.period === 'weekly'
                          ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                          : 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                      color: '#fff',
                      fontFamily: 'Lilita One',
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {periodLabel}
                  </span>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      background:
                        reward.leaderboardType === 'pve'
                          ? 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)'
                          : 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                      color: '#fff',
                      fontFamily: 'Lilita One',
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {modeLabel}
                  </span>
                </motion.div>

                {/* Title */}
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h2
                    className="text-lg text-white/80 mb-1"
                    style={{
                      fontFamily: 'Lilita One',
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    Congratulations!
                  </h2>
                  <h1
                    className="text-3xl"
                    style={{
                      fontFamily: 'Lilita One',
                      color: tier.color,
                      textShadow: `0 0 20px ${tier.glowColor}, 2px 3px 0px #000`,
                      WebkitTextStroke: '1.5px #000',
                    }}
                  >
                    #{reward.rank} {tier.title}
                  </h1>
                </motion.div>

                {/* Divider */}
                <motion.div
                  className="w-3/4 h-px"
                  style={{
                    background: `linear-gradient(90deg, transparent 0%, ${tier.color} 50%, transparent 100%)`,
                  }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.5 }}
                />

                {/* Rewards Container */}
                <motion.div
                  className="w-full flex flex-col gap-3 p-4 rounded-2xl"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <p
                    className="text-center text-white/60 text-sm"
                    style={{ fontFamily: 'Lilita One' }}
                  >
                    Your Rewards
                  </p>

                  {/* Gold Reward */}
                  <motion.div
                    className="flex items-center justify-between px-4 py-3 rounded-xl"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                    }}
                    initial={{ x: -30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    <div className="flex items-center gap-3">
                      <img src={goldIcon} alt="Gold" className="w-8 h-8" />
                      <span
                        className="text-white"
                        style={{ fontFamily: 'Lilita One', fontSize: 14 }}
                      >
                        Gold Bonus
                      </span>
                    </div>
                    <motion.span
                      style={{
                        fontFamily: 'Lilita One',
                        fontSize: 22,
                        color: '#fbbf24',
                        textShadow:
                          '0 0 10px rgba(251, 191, 36, 0.5), 1px 2px 0px #000',
                      }}
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.2, 1] }}
                      transition={{ delay: 0.8 }}
                    >
                      +{reward.gold.toLocaleString()}
                    </motion.span>
                  </motion.div>

                  {/* Energy Boost Reward */}
                  <motion.div
                    className="flex items-center justify-between px-4 py-3 rounded-xl"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(6, 182, 212, 0.1) 100%)',
                      border: '1px solid rgba(34, 211, 238, 0.3)',
                    }}
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.8 }}
                  >
                    <div className="flex items-center gap-3">
                      <img src={energyIcon} alt="Energy" className="w-8 h-8" />
                      <div className="flex flex-col">
                        <span
                          className="text-white"
                          style={{ fontFamily: 'Lilita One', fontSize: 14 }}
                        >
                          Max Energy Boost
                        </span>
                        <span
                          className="text-white/50"
                          style={{ fontFamily: 'Lilita One', fontSize: 11 }}
                        >
                          {reward.durationDays} days
                        </span>
                      </div>
                    </div>
                    <motion.span
                      style={{
                        fontFamily: 'Lilita One',
                        fontSize: 22,
                        color: '#22d3ee',
                        textShadow:
                          '0 0 10px rgba(34, 211, 238, 0.5), 1px 2px 0px #000',
                      }}
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.2, 1] }}
                      transition={{ delay: 0.9 }}
                    >
                      +{reward.energyBoost}
                    </motion.span>
                  </motion.div>
                </motion.div>

                {/* Claim Button */}
                <motion.button
                  onClick={onClose}
                  className="relative w-full py-4 rounded-xl overflow-hidden"
                  style={{
                    background: tier.bgGradient,
                    border: '3px solid #000',
                    boxShadow: `0 0 20px ${tier.glowColor}, 0 4px 0 #000`,
                  }}
                  whileHover={{
                    scale: 1.02,
                    boxShadow: `0 0 30px ${tier.glowColor}, 0 6px 0 #000`,
                  }}
                  whileTap={{ scale: 0.98, y: 2 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  {/* Button shine */}
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                    }}
                    initial={{ x: '-100%' }}
                    animate={{ x: '200%' }}
                    transition={{
                      delay: 1.2,
                      duration: 0.8,
                      repeat: Infinity,
                      repeatDelay: 2,
                    }}
                  />
                  {/* Button top highlight */}
                  <div className="absolute inset-x-3 top-2 h-6 bg-white/30 rounded-lg" />
                  <span
                    className="relative text-2xl"
                    style={{
                      fontFamily: 'Lilita One',
                      color: reward.rank === 1 ? '#1a1a2e' : '#fff',
                      textShadow:
                        reward.rank === 1 ? 'none' : '2px 3px 0px #000',
                      WebkitTextStroke: reward.rank === 1 ? 'none' : '1px #000',
                    }}
                  >
                    Awesome!
                  </span>
                </motion.button>

                {/* Footer text */}
                <motion.p
                  className="text-white/40 text-xs text-center"
                  style={{ fontFamily: 'Lilita One' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.1 }}
                >
                  Keep climbing the ranks for more rewards!
                </motion.p>
              </div>
            </motion.div>
          </div>

          {/* Confetti - Highest z-index */}
          <div className="fixed inset-0 z-[125] pointer-events-none">
            <Confetti
              width={width}
              height={height}
              recycle={false}
              numberOfPieces={400}
              gravity={0.2}
              colors={[
                '#FFD700',
                '#FFA500',
                '#FF8C00',
                '#a855f7',
                '#3b82f6',
                '#22c55e',
                '#fff',
              ]}
            />
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
